import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import {
  type ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  type Row,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';

import { type DatasetsQueryResult, useDatasets } from '@/api/generated';
import { DataTable } from '@/components/blocks/data-table/data-table';
import Header from '@/components/header';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Spinner } from '@/components/ui/spinner';
import { useOpen } from '@/hooks/use-open';
import CreateDatasetDialog from '@/routes/(app)/(datasets)/components/create-dataset-dialog';
import DatasetExpansion from '@/routes/(app)/(datasets)/components/dataset-expansion';
import DatasetFilters from '@/routes/(app)/(datasets)/components/dataset-filters';
import { columns } from '@/routes/(app)/(datasets)/components/datasets-columns';
import {
  type DatasetFiltersSchema,
  datasetFiltersSchema,
} from '@/routes/(app)/(datasets)/lib/schema';

type Dataset = DatasetsQueryResult['data'][number];

const route = getRouteApi('/app/datasets');

const Datasets = () => {
  const { t } = useTranslation();
  const { task, mimeTypes, isEval } = route.useSearch();
  const navigate = useNavigate();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'createdAt', desc: false },
  ]);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const form = useForm<DatasetFiltersSchema>({
    resolver: zodResolver(datasetFiltersSchema),
    defaultValues: {
      task,
      mimeTypes,
      isEval: !!isEval,
    },
  });

  const { open: openCreateDataset, onOpenChange: onOpenChangeCreateDataset } =
    useOpen({
      defaultOpen: false,
    });

  const handleOpenCreateDataset = useCallback(() => {
    onOpenChangeCreateDataset(true);
  }, [onOpenChangeCreateDataset]);

  const { data, isLoading, refetch } = useDatasets({
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    order: sorting[0]?.desc ? 'desc' : 'asc',
    orderBy: sorting[0]?.id,
    task,
    mimeTypes,
    isEval: !!isEval,
  });

  const table = useReactTable<Dataset>({
    state: {
      pagination,
      sorting,
      expanded,
    },
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    manualPagination: true,
    pageCount: Math.ceil((data?.total ?? 0) / pagination.pageSize),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    manualSorting: true,
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpanded,
    getRowCanExpand: (row) => row.original.instances > 0,
  });

  const handleRowClick = useCallback(
    async (row: Row<Dataset>) => {
      await navigate({
        to: '/app/datasets/$datasetId',
        params: { datasetId: row.original.id },
      });
    },
    [navigate]
  );

  return (
    <Form {...form}>
      <Header
        title={t('datasets.title')}
        description={t('datasets.description')}
      />

      <div className='flex flex-row justify-between items-end'>
        <DatasetFilters />
        <Button onClick={handleOpenCreateDataset}>
          {t('datasets.createDataset.button')}
        </Button>
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <DataTable
          table={table}
          onRowClick={handleRowClick}
          Expansion={DatasetExpansion}
        />
      )}

      <CreateDatasetDialog
        open={openCreateDataset}
        onOpenChange={onOpenChangeCreateDataset}
        refetch={refetch}
      />
    </Form>
  );
};

export default Datasets;
